<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import store from "@/store";

import { toast } from "@/common/utils/toast";

import {
  franchiseList,
  franchiseAdd,
  franchiseDel,
} from "@/common/api/rest/franchise";

import type {
  FranchiseType,
  FranchisePayloadType,
} from "@/common/types/franchise";

import { FormFranchise } from "@/components";
import {
  breadcrumbSimple,
  baseConfirm,
  baseModal,
  baseTable,
  svgIcon,
} from "@/common/components";

const { t } = useI18n();
const router = useRouter();

const tableData = ref<FranchiseType[]>([]);
const payload = ref<FranchisePayloadType | null>(null);

const selected = ref<FranchiseType | null>(null);

const limit = ref<number>(10);
const page = ref<number>(0);
const pages = ref<number>(0);
const search = ref<string>("");
const searched = ref<number>(0);
const total = ref<number>(0);

const showModalAdd = ref(false);
const showModalDel = ref(false);
const payloadValid = ref(false);

const tableHeader = computed(() => [
  { key: "name", label: t("common.labels.name") },
  { key: "status", label: t("common.labels.status") },
]);

const breadcrumbList = computed(() => [
  {
    key: "register",
    label: t("common.labels.registrations"),
    url: "/register",
  },
  {
    key: "franchise",
    label: t("franchise.labels.franchises"),
    url: `/register/franchise`,
  },
]);

const status = computed(() => ({
  ACTIVE: t("common.labels.active"),
  INACTIVE: t("common.labels.inactive"),
}));

function fetchData() {
  franchiseList({
    page: page.value,
    limit: limit.value,
    search: search.value,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        tableData.value = data.list;
        pages.value = data.infos.pages;
        total.value = data.infos.total;
        searched.value = data.infos.count;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function changePage(value: number) {
  page.value = value;
  fetchData();
}

function searchItem(value: string) {
  search.value = value;

  fetchData();
}

function addItem() {
  showModalAdd.value = true;
}

function addItemCancel() {
  showModalAdd.value = false;
  payload.value = null;
}

function addItemConfirm() {
  if (!payload.value) return;

  store.commit("SET_LOADING", true);

  franchiseAdd(payload.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        editItem(data);
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function editItem(data: FranchiseType) {
  // router.push(`/register/franchise/${data.franchise_id}/edit`);
  window.open(`/register/franchise/${data.franchise_id}/edit`, "_blank");
}

function removeItem(data: FranchiseType) {
  selected.value = data;
  showModalDel.value = true;
}

function removeItemCancel() {
  showModalDel.value = false;
  selected.value = null;
}

function removeItemConfirm() {
  if (!selected.value) return;

  franchiseDel(selected.value.franchise_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
        removeItemCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

onMounted(() => {
  fetchData();
});
</script>

<template>
  <article class="register-page">
    <section class="page-header">
      <breadcrumb-simple
        :items="breadcrumbList"
        :show-home="true"
        url-home="/home"
        active="franchise"
      />
    </section>

    <section class="page-content">
      <div class="tab-header">
        <svg-icon icon="no-image" size="2rem" />
        {{ t("franchise.labels.franchises") }}
      </div>

      <div class="tab-content">
        <base-table
          :header="tableHeader"
          :data="tableData"
          :limit="limit"
          :page="page"
          :pages="pages"
          :total="total"
          :searched="searched"
          :options="true"
          :del="true"
          :edit="true"
          @change:page="changePage($event)"
          @searching="searchItem($event)"
          @btn:add="addItem()"
          @btn:edit="editItem($event)"
          @btn:del="removeItem($event)"
        >
          <template #status="{ data }">
            {{ status[data.status]?.toUpperCase() }}
          </template>
        </base-table>
      </div>
    </section>
  </article>

  <base-modal
    :show="showModalAdd"
    :title="t('franchise.modals.titles.add_franchise').toUpperCase()"
    :show-button-confirm="true"
    :button-confirm="t('common.labels.save')"
    :button-close="t('common.labels.cancel')"
    @close="addItemCancel()"
    @confirm="addItemConfirm()"
  >
    <template #content>
      <form-franchise
        @update:data="payload = $event"
        @validate="payloadValid = $event"
      />
    </template>
  </base-modal>

  <base-confirm
    :show="showModalDel"
    :title="t('franchise.modals.titles.del_franchise').toUpperCase()"
    :message="
      t('franchise.modals.messages.del_franchise', {
        name: selected?.name?.toUpperCase(),
      })
    "
    @cancel="removeItemCancel()"
    @confirm="removeItemConfirm()"
  />
</template>

<style lang="scss" scoped>
.register-page {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .page-header {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: inherit;
    background: white;
    border-radius: 0.5rem;
    padding-inline: 1rem;
    padding-block: 0.5rem;
  }

  .page-content {
    display: flex;
    flex-direction: column;
    background: white;
    border-radius: 0.5rem;

    .tab-header {
      display: inline-flex;
      gap: 0.5rem;
      font-size: 1.2rem;
      align-items: center;
      padding-inline: 1.25rem;
      padding-block: 0.75rem;
      border-block-end: 1px solid var(--border);
      text-transform: uppercase;
    }

    .tab-content {
      display: flex;
      flex-direction: column;
      padding-inline: 1.5rem;
      padding-block: 1rem;
      gap: 1rem;
    }
  }
}
</style>
